import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to format UTC timestamp to local time
const formatLocalTime = (utcTimestamp, includeDate = true) => {
  if (!utcTimestamp) return '';
  try {
    const date = new Date(utcTimestamp);
    if (isNaN(date.getTime())) return utcTimestamp;
    
    if (includeDate) {
      return date.toLocaleString('en-NZ', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } else {
      return date.toLocaleTimeString('en-NZ', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    }
  } catch {
    return utcTimestamp;
  }
};

export default function DebugCallData() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedOrigins, setExpandedOrigins] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    trace: false, config: true, origins: true, raw: false, webhooks: false
  });
  
  // Call trace state
  const [traceNumber, setTraceNumber] = useState('');
  const [traceHours, setTraceHours] = useState(24);
  const [traceResults, setTraceResults] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/debug/raw-call-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('Failed to fetch debug data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [fetchData, autoRefresh]);

  const toggleOrigin = (oid) => {
    setExpandedOrigins(prev => ({ ...prev, [oid]: !prev[oid] }));
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const traceCall = async () => {
    if (!traceNumber.trim()) return;
    setTraceLoading(true);
    try {
      const res = await fetch(`${API}/api/webhooks/call-trace/${encodeURIComponent(traceNumber.trim())}?hours=${traceHours}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      setTraceResults(json);
    } catch (err) {
      console.error('Failed to trace call:', err);
      setTraceResults({ error: err.message });
    } finally {
      setTraceLoading(false);
    }
  };

  const stateColor = (state) => {
    const s = (state || '').toLowerCase();
    if (s === 'ringing') return '#f59e0b';
    if (s === 'answered' || s === 'connected') return '#10b981';
    if (s === 'ended' || s === 'hangup' || s === 'completed') return '#6b7280';
    if (s === 'dismissed') return '#ef4444';
    if (s === 'noanswer' || s === 'missed' || s === 'busy') return '#f87171';
    return '#94a3b8';
  };

  const targetColor = (type) => {
    if (type === 'USER_EXTENSION') return '#3b82f6';
    if (type === 'DDI') return '#a855f7';
    if (type === 'COMPANY_PREFIX') return '#06b6d4';
    return '#94a3b8';
  };

  if (loading) return <div style={styles.container}><p style={styles.text}>Loading raw data...</p></div>;
  if (!data) return <div style={styles.container}><p style={styles.text}>No data available</p></div>;

  return (
    <div style={styles.container} data-testid="debug-page">
      <div style={styles.header}>
        <h1 style={styles.title}>Raw Call Data Inspector</h1>
        <div style={styles.controls}>
          <button
            onClick={fetchData}
            style={styles.btn}
            data-testid="refresh-btn"
          >
            Refresh Now
          </button>
          <label style={styles.label}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              data-testid="auto-refresh-toggle"
            />
            Auto-refresh (3s)
          </label>
          <span style={styles.timestamp}>Updated: {formatLocalTime(data.timestamp, false)}</span>
        </div>
      </div>

      {/* SECTION: Call Trace */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle} onClick={() => toggleSection('trace')}>
          {expandedSections.trace ? '▼' : '▶'} 🔍 Call Trace (Debug specific calls)
        </h2>
        {expandedSections.trace && (
          <div style={styles.traceSection}>
            <div style={styles.traceForm}>
              <input
                type="text"
                placeholder="Enter phone number to trace (e.g., 272222995)"
                value={traceNumber}
                onChange={(e) => setTraceNumber(e.target.value)}
                style={styles.input}
                data-testid="trace-number-input"
              />
              <select 
                value={traceHours} 
                onChange={(e) => setTraceHours(parseInt(e.target.value))}
                style={styles.select}
              >
                <option value={1}>Last 1 hour</option>
                <option value={6}>Last 6 hours</option>
                <option value={12}>Last 12 hours</option>
                <option value={24}>Last 24 hours</option>
                <option value={48}>Last 48 hours</option>
                <option value={72}>Last 72 hours</option>
              </select>
              <button 
                onClick={traceCall} 
                style={styles.btn}
                disabled={traceLoading || !traceNumber.trim()}
                data-testid="trace-btn"
              >
                {traceLoading ? 'Tracing...' : 'Trace Call'}
              </button>
            </div>
            
            {traceResults && (
              <div style={styles.traceResults}>
                {traceResults.error ? (
                  <p style={{ color: '#ef4444' }}>Error: {traceResults.error}</p>
                ) : (
                  <>
                    <div style={styles.traceSummary}>
                      <span style={styles.traceLabel}>Phone: <strong>{traceResults.phone_number}</strong></span>
                      <span style={styles.traceLabel}>Events: <strong>{traceResults.total_events}</strong></span>
                      <span style={styles.traceLabel}>Call Groups: <strong>{traceResults.call_groups}</strong></span>
                    </div>
                    
                    {Object.entries(traceResults.grouped_by_origin || {}).map(([originId, events]) => {
                      const hasAnswered = events.some(e => e.state?.toLowerCase() === 'answered');
                      const hasRinging = events.some(e => e.state?.toLowerCase() === 'ringing');
                      const hasMissed = events.some(e => ['missed', 'noanswer'].includes(e.state?.toLowerCase()));
                      
                      return (
                        <div key={originId} style={{
                          ...styles.traceGroup,
                          borderLeft: `4px solid ${hasAnswered ? '#10b981' : hasMissed ? '#ef4444' : hasRinging ? '#f59e0b' : '#6b7280'}`
                        }}>
                          <div style={styles.traceGroupHeader}>
                            <span style={styles.mono}>Origin: {originId}</span>
                            <div style={styles.badges}>
                              <span style={styles.badge}>{events.length} events</span>
                              {hasAnswered && <span style={{ ...styles.badge, background: '#10b981' }}>ANSWERED</span>}
                              {hasMissed && !hasAnswered && <span style={{ ...styles.badge, background: '#ef4444' }}>MISSED</span>}
                            </div>
                          </div>
                          <table style={styles.table}>
                            <thead>
                              <tr>
                                <th style={styles.th}>To</th>
                                <th style={styles.th}>From</th>
                                <th style={styles.th}>State</th>
                                <th style={styles.th}>State History</th>
                                <th style={styles.th}>Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {events.sort((a, b) => (a.received_at || '').localeCompare(b.received_at || '')).map((event, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                                  <td style={styles.td}><code>{event.to_number}</code></td>
                                  <td style={styles.td}><code>{event.from_number}</code></td>
                                  <td style={styles.td}>
                                    <span style={{ ...styles.stateBadge, background: stateColor(event.state) }}>
                                      {event.state}
                                    </span>
                                    {event.late_ringing && <span style={{ marginLeft: 4, color: '#f59e0b', fontSize: 10 }}>(late)</span>}
                                    {event.connected_by_other && <span style={{ marginLeft: 4, color: '#6b7280', fontSize: 10 }}>(other answered)</span>}
                                  </td>
                                  <td style={styles.td}>
                                    <span style={styles.mono}>
                                      {(event.state_history || []).map(h => typeof h === 'object' ? h.state : h).join(' → ')}
                                    </span>
                                  </td>
                                  <td style={styles.td}>
                                    <span style={styles.mono}>{formatLocalTime(event.received_at)}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                    
                    {traceResults.total_events === 0 && (
                      <p style={styles.muted}>No events found for this number in the last {traceHours} hours.</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* SECTION: Config */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle} onClick={() => toggleSection('config')}>
          {expandedSections.config ? '▼' : '▶'} Company Config & Matching Rules
        </h2>
        {expandedSections.config && (
          <div style={styles.grid}>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Company</h3>
              <p style={styles.mono}>{data.company?.name}</p>
              <p style={styles.mono}>Account Prefix: <strong>{data.account_prefix}</strong></p>
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Known Extensions ({Object.keys(data.known_extensions || {}).length})</h3>
              {Object.entries(data.known_extensions || {}).map(([num, name]) => (
                <p key={num} style={styles.mono}>{num} {name && `(${name})`}</p>
              ))}
              {Object.keys(data.known_extensions || {}).length === 0 && <p style={styles.muted}>None registered</p>}
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Known DDIs ({(data.known_ddis || []).length})</h3>
              {(data.known_ddis || []).map(d => (
                <p key={d} style={styles.mono}>{d}</p>
              ))}
              {(data.known_ddis || []).length === 0 && <p style={styles.muted}>None cached</p>}
            </div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Dismissal Rule</h3>
              <p style={{ ...styles.mono, color: '#f59e0b' }}>{data.dismissal_rule}</p>
            </div>
          </div>
        )}
      </div>

      {/* SECTION: Origin Groups */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle} onClick={() => toggleSection('origins')}>
          {expandedSections.origins ? '▼' : '▶'} Call Origins ({Object.keys(data.origin_groups || {}).length} groups, {data.unique_event_keys} event keys)
        </h2>
        {expandedSections.origins && (
          <div>
            {Object.entries(data.origin_groups || {}).map(([oid, legs]) => {
              const hasRinging = legs.some(l => l.state?.toLowerCase() === 'ringing');
              const hasAnswered = legs.some(l => l.state?.toLowerCase() === 'answered');
              const allTerminal = legs.every(l =>
                ['ended', 'hangup', 'completed', 'noanswer', 'missed', 'busy', 'failed', 'dismissed'].includes(l.state?.toLowerCase())
              );
              return (
                <div key={oid} style={{
                  ...styles.originCard,
                  borderLeft: `4px solid ${allTerminal ? '#4b5563' : hasRinging ? '#f59e0b' : '#10b981'}`,
                  opacity: allTerminal ? 0.5 : 1
                }}>
                  <div
                    style={styles.originHeader}
                    onClick={() => toggleOrigin(oid)}
                    data-testid={`origin-${oid.slice(0, 12)}`}
                  >
                    <span style={styles.mono}>
                      <strong>Origin:</strong> {oid}
                    </span>
                    <span style={styles.badges}>
                      <span style={styles.badge}>{legs.length} legs</span>
                      {hasRinging && <span style={{ ...styles.badge, background: '#f59e0b', color: '#000' }}>RINGING</span>}
                      {hasAnswered && <span style={{ ...styles.badge, background: '#10b981' }}>ANSWERED</span>}
                      {allTerminal && <span style={{ ...styles.badge, background: '#4b5563' }}>ENDED</span>}
                    </span>
                  </div>
                  {(expandedOrigins[oid] || !allTerminal) && (
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>To Number</th>
                          <th style={styles.th}>Target Type</th>
                          <th style={styles.th}>From</th>
                          <th style={styles.th}>Current State</th>
                          <th style={styles.th}>State History</th>
                          <th style={styles.th}>Triggers Dismissal?</th>
                          <th style={styles.th}>Received</th>
                        </tr>
                      </thead>
                      <tbody>
                        {legs.map((leg, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #1e293b' }}>
                            <td style={styles.td}><code>{leg.to_number}</code></td>
                            <td style={styles.td}>
                              <span style={{ ...styles.typeBadge, background: targetColor(leg.target_type) }}>
                                {leg.target_type}
                              </span>
                            </td>
                            <td style={styles.td}><code>{leg.from_number}</code></td>
                            <td style={styles.td}>
                              <span style={{ ...styles.stateBadge, background: stateColor(leg.state) }}>
                                {leg.state}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <span style={styles.mono}>
                                {(leg.state_history || []).join(' → ')}
                              </span>
                            </td>
                            <td style={styles.td}>
                              {leg.triggers_dismissal ? (
                                <span style={{ color: '#ef4444', fontWeight: 'bold' }}>YES</span>
                              ) : (
                                <span style={{ color: '#6b7280' }}>no</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              <span style={styles.mono}>{formatLocalTime(leg.received_at, false)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* SECTION: Raw Events */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle} onClick={() => toggleSection('raw')}>
          {expandedSections.raw ? '▼' : '▶'} Raw Events (last {data.raw_events_count} in 2hr window, showing 50)
        </h2>
        {expandedSections.raw && (
          <div style={styles.codeBlock}>
            <pre style={styles.pre}>
              {JSON.stringify(data.raw_events, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* SECTION: Webhook Payloads */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle} onClick={() => toggleSection('webhooks')}>
          {expandedSections.webhooks ? '▼' : '▶'} Recent Webhook Payloads ({(data.recent_webhook_payloads || []).length})
        </h2>
        {expandedSections.webhooks && (
          <div style={styles.codeBlock}>
            <pre style={styles.pre}>
              {JSON.stringify(data.recent_webhook_payloads, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { background: '#0a0e1a', color: '#e2e8f0', minHeight: '100vh', padding: '20px', fontFamily: 'monospace' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  controls: { display: 'flex', alignItems: 'center', gap: 16 },
  btn: { background: '#2563eb', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  label: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#94a3b8' },
  timestamp: { fontSize: 12, color: '#64748b' },
  section: { marginBottom: 24 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#cbd5e1', cursor: 'pointer', padding: '8px 0', borderBottom: '1px solid #1e293b', userSelect: 'none' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginTop: 12 },
  card: { background: '#111827', border: '1px solid #1e293b', borderRadius: 8, padding: 16 },
  cardTitle: { fontSize: 13, fontWeight: 600, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 },
  mono: { fontFamily: 'monospace', fontSize: 13, color: '#e2e8f0', margin: '4px 0' },
  muted: { color: '#4b5563', fontSize: 13 },
  text: { color: '#94a3b8', fontSize: 14 },
  originCard: { background: '#111827', border: '1px solid #1e293b', borderRadius: 8, marginBottom: 8, overflow: 'hidden' },
  originHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', cursor: 'pointer' },
  badges: { display: 'flex', gap: 6 },
  badge: { fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#1e293b', color: '#e2e8f0', fontWeight: 600 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '6px 12px', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #1e293b', fontSize: 11, textTransform: 'uppercase' },
  td: { padding: '6px 12px', verticalAlign: 'middle' },
  stateBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, color: '#fff' },
  typeBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff' },
  codeBlock: { background: '#111827', border: '1px solid #1e293b', borderRadius: 8, padding: 16, marginTop: 12, maxHeight: 500, overflow: 'auto' },
  pre: { margin: 0, fontSize: 11, color: '#94a3b8', whiteSpace: 'pre-wrap', wordBreak: 'break-all' },
  // Call trace styles
  traceSection: { background: '#111827', border: '1px solid #1e293b', borderRadius: 8, padding: 16, marginTop: 12 },
  traceForm: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  input: { flex: 1, minWidth: 200, background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '8px 12px', color: '#e2e8f0', fontSize: 14, fontFamily: 'monospace' },
  select: { background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '8px 12px', color: '#e2e8f0', fontSize: 14 },
  traceResults: { marginTop: 16 },
  traceSummary: { display: 'flex', gap: 24, marginBottom: 16, padding: '12px 16px', background: '#0f172a', borderRadius: 6 },
  traceLabel: { fontSize: 13, color: '#94a3b8' },
  traceGroup: { background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, marginBottom: 12, overflow: 'hidden' },
  traceGroupHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: '#1e293b' },
};
